import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import API, { clearClientSession } from '../services/api';

const AuthContext = createContext();
const IDLE_TIMEOUT_MINUTES = Number(process.env.REACT_APP_IDLE_TIMEOUT_MINUTES || 60);
const SESSION_MAX_HOURS = Number(process.env.REACT_APP_SESSION_MAX_HOURS || 24);
const LAST_ACTIVITY_KEY = 'last_activity_at';
const SESSION_START_KEY = 'auth_session_started_at';
const AUTH_STATE_EVENT = 'auth-state-changed';
const AUTH_ROUTES = ['/login', '/forgot-password', '/set-password', '/auth-callback'];

const getNow = () => Date.now();

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(() => {
    const storedSession = localStorage.getItem("session");
    if (storedSession) {
      try {
        return JSON.parse(storedSession);
      } catch (error) {
        console.error("Error parsing stored session:", error);
        return null;
      }
    }
    return null;
  });
  
  const [role, setRole] = useState(null);
  const [tenant, setTenant ] = useState(null);
  const [availableFeatures, setAvailableFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRoleToken, setLastRoleToken] = useState(null);

  const readAuthFromStorage = useCallback(() => {
    const token = localStorage.getItem("token");
    const storedSession = localStorage.getItem("session");
    let parsedSession = null;

    if (storedSession) {
      try {
        parsedSession = JSON.parse(storedSession);
      } catch (error) {
        parsedSession = null;
      }
    }

    if (parsedSession?.access_token) {
      return { session: parsedSession, token: parsedSession.access_token };
    }

    if (token) {
      const fallbackSession = { access_token: token };
      localStorage.setItem("session", JSON.stringify(fallbackSession));
      return { session: fallbackSession, token };
    }

    return { session: null, token: null };
  }, []);

  const clearClientAuthState = useCallback(async () => clearClientSession(), []);

  const terminateSession = useCallback(async () => {
    if (!await clearClientAuthState()) return;
    if (window.location.pathname !== '/login') window.location.href = '/login';
  }, [clearClientAuthState]);

  const fetchRole = useCallback(async (token) => {
    if (!token || token === lastRoleToken) return;

    try {
      const response = await API.get('/get-role/');
      setRole(response.data?.role || null);
      setTenant(response.data?.tenantName || null);
      setAvailableFeatures(response.data?.availableFeatures || []);
      setLastRoleToken(localStorage.getItem('token') || token);
      return response.data;
    } catch (error) {
      console.error("Error fetching role:", error);
      setRole(null);
      setTenant(null);
      setAvailableFeatures([]);
      setLastRoleToken(null);
      return null;
    }
  }, [lastRoleToken]);

  useEffect(() => {
    let isMounted = true;

    const updateSession = async (force = false) => {
      const pathname = window.location.pathname || '';
      const isAuthRoute = AUTH_ROUTES.some((route) => pathname.startsWith(route));
      if (isAuthRoute && !force) {
        setLoading(false);
        return;
      }

      const { session: storedSession, token } = readAuthFromStorage();

      if (!isMounted) return;

      if (token) {
        setSession(storedSession);
        await fetchRole(token);
      } else {
        localStorage.removeItem("session");
        localStorage.removeItem("token");
        localStorage.removeItem(LAST_ACTIVITY_KEY);
        localStorage.removeItem(SESSION_START_KEY);
        setSession(null);
        setRole(null);
        setTenant(null);
        setAvailableFeatures([]);
        setLastRoleToken(null);
      }

      if (isMounted) setLoading(false);
    };

    updateSession();

    const handleAuthStateSync = () => {
      updateSession();
    };

    const handleForcedAuthSync = () => {
      updateSession(true);
    };

    window.addEventListener("storage", handleAuthStateSync);
    window.addEventListener(AUTH_STATE_EVENT, handleForcedAuthSync);
    window.addEventListener("focus", handleAuthStateSync);

    return () => {
      isMounted = false;
      window.removeEventListener("storage", handleAuthStateSync);
      window.removeEventListener(AUTH_STATE_EVENT, handleForcedAuthSync);
      window.removeEventListener("focus", handleAuthStateSync);
    };
  }, [fetchRole, readAuthFromStorage]);

  useEffect(() => {
    if (!session) return;

    const now = getNow();
    if (!localStorage.getItem(SESSION_START_KEY)) {
      localStorage.setItem(SESSION_START_KEY, String(now));
    }
    if (!localStorage.getItem(LAST_ACTIVITY_KEY)) {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    }

    const markActivity = () => {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(getNow()));
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((eventName) => window.addEventListener(eventName, markActivity, { passive: true }));
    document.addEventListener('visibilitychange', markActivity);

    const intervalId = setInterval(async () => {
      const nowTime = getNow();
      const lastActivity = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || nowTime);
      const sessionStartedAt = Number(localStorage.getItem(SESSION_START_KEY) || nowTime);

      const idleMs = nowTime - lastActivity;
      const sessionAgeMs = nowTime - sessionStartedAt;
      const maxIdleMs = IDLE_TIMEOUT_MINUTES * 60 * 1000;
      const maxSessionMs = SESSION_MAX_HOURS * 60 * 60 * 1000;

      if (idleMs >= maxIdleMs || sessionAgeMs >= maxSessionMs) {
        await terminateSession();
      }
    }, 30000);

    return () => {
      clearInterval(intervalId);
      events.forEach((eventName) => window.removeEventListener(eventName, markActivity));
      document.removeEventListener('visibilitychange', markActivity);
    };
  }, [session, terminateSession]);

  const updateVerifiedRole = useCallback((newRole, newFeatures = []) => {
    setRole(newRole);
    setAvailableFeatures(newFeatures);
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    session,
    role,
    tenant,
    availableFeatures,
    loading,
    updateVerifiedRole,
    terminateSession,
  }), [session, role, tenant, availableFeatures, loading, updateVerifiedRole, terminateSession]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
